import { useState } from 'react';
import { Loader2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { getAvailableYears } from '@/lib/dateUtils';
import { useToast } from '@/hooks/use-toast';
import { exportPersonReportExcel } from '../utils/exportPersonReport';
import type { PersonReportSubject } from '../utils/exportPersonReport';

interface Props {
  subject: PersonReportSubject;
  /** Year the matrix is currently showing; the range starts there. */
  defaultYear: number;
  onClose: () => void;
}

const SELECT = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

/** Pick a year range and download one person's/entity's tithe record. */
export function TitheReportDialog({ subject, defaultYear, onClose }: Props) {
  const { toast } = useToast();
  const years = getAvailableYears();
  const oldest = years[years.length - 1];
  const [fromYear, setFromYear] = useState(defaultYear);
  const [toYear, setToYear]     = useState(defaultYear);
  const [busy, setBusy]         = useState(false);

  const invalid = fromYear > toYear;

  const handleDownload = async () => {
    if (invalid) return;
    setBusy(true);
    try {
      await exportPersonReportExcel(subject, fromYear, toYear);
      onClose();
    } catch (e) {
      console.error(e);
      toast({ title: 'No se pudo generar el reporte', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reporte de diezmos</DialogTitle>
          <DialogDescription>
            {subject.label}{subject.roleLabel && ` — ${subject.roleLabel}`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium">Desde</label>
              <select value={fromYear} onChange={(e) => setFromYear(parseInt(e.target.value))} disabled={busy} className={SELECT}>
                {[...years].reverse().map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium">Hasta</label>
              <select value={toYear} onChange={(e) => setToYear(parseInt(e.target.value))} disabled={busy} className={SELECT}>
                {[...years].reverse().map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
          <button
            type="button" disabled={busy}
            onClick={() => { setFromYear(oldest); setToYear(new Date().getFullYear()); }}
            className="text-sm text-primary hover:underline"
          >
            Todo el historial
          </button>
          {invalid && <p className="text-xs text-destructive">«Desde» no puede ser mayor que «Hasta».</p>}
          <p className="text-xs text-muted-foreground">
            Incluye los meses pagados, con retraso y pendientes dentro de su gestión.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="button" onClick={handleDownload} disabled={busy || invalid}>
            {busy ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Download className="mr-1.5 size-4" />}
            Descargar Excel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
